import React, { useState, useEffect, useRef } from "react";
import { ShieldCheck, Upload, FileCheck2, Loader2, Clock } from "lucide-react";
import { getMyChecks, uploadMyComplianceDoc } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { SectionCard } from "./PortalShared";

const statusLabel = {
  "verification-sent": "Action needed — please upload your documents",
  "documents-received": "Documents received — under review",
  verified: "Verified ✓", failed: "Needs manual review", pending: "Awaiting setup",
};
const statusColor = {
  "verification-sent": "bg-amber-100 text-amber-700", "documents-received": "bg-blue-100 text-blue-700",
  verified: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-600", pending: "bg-gray-100 text-gray-500",
};

const UploadTile = ({ label, docType, onUpload, uploaded, busy }) => {
  const ref = useRef(null);
  return (
    <div className="p-4 rounded-xl border border-[#f0ece5] bg-[#faf7f2]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[14px] font-500 text-[#2b2b2b] flex items-center gap-2">{uploaded ? <FileCheck2 size={16} className="text-green-600" /> : <Clock size={16} className="text-[#b8935f]" />} {label}</p>
        {uploaded && <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">Uploaded</span>}
      </div>
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`upload-${docType}-btn`} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploaded ? "Replace file" : "Upload file"}
      </button>
      <input ref={ref} type="file" accept="image/*,application/pdf" hidden data-testid={`upload-${docType}-input`} onChange={(e) => { if (e.target.files?.[0]) onUpload(e.target.files[0], docType); e.target.value = ""; }} />
    </div>
  );
};

const PortalCompliance = () => {
  const { toast } = useToast();
  const [checks, setChecks] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = () => getMyChecks().then(setChecks).catch(() => setChecks([]));
  useEffect(() => { refresh(); }, []);

  const upload = async (file, docType) => {
    setBusy(docType);
    try { await uploadMyComplianceDoc(file, docType); toast({ title: "Document uploaded securely", description: "Your agent will review it shortly." }); refresh(); }
    catch (err) { toast({ title: "Upload failed", description: err?.response?.data?.detail || "Please try again." }); }
    finally { setBusy(null); }
  };

  const check = checks[0];
  const docs = check?.documents || [];
  const hasType = (t) => docs.some((d) => d.type === t);
  const isRTR = check ? check.type === "right-to-rent" : true;

  return (
    <SectionCard title="Identity & Compliance">
      <div className="flex items-start gap-3 mb-6 p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="portal-compliance">
        <ShieldCheck size={22} className="text-[#b8935f] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[14px] text-[#2b2b2b] font-500">{isRTR ? "Right to Rent" : "Identity & AML"} verification</p>
          <p className="text-[13px] text-[#6a6a6a] mt-1">Securely upload your documents so we can complete the legally-required checks. Files are encrypted and only visible to your Anadear agent.</p>
          {check && <span className={`inline-block mt-2 text-[11px] px-2.5 py-1 rounded-full ${statusColor[check.status] || "bg-gray-100"}`}>{statusLabel[check.status] || check.status}</span>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <UploadTile label="Photo ID (passport / licence)" docType="id" onUpload={upload} uploaded={hasType("id")} busy={busy === "id"} />
        <UploadTile label="Proof of Address" docType="proof-of-address" onUpload={upload} uploaded={hasType("proof-of-address")} busy={busy === "proof-of-address"} />
        {isRTR && <UploadTile label="Visa / Share Code (if applicable)" docType="visa" onUpload={upload} uploaded={hasType("visa")} busy={busy === "visa"} />}
      </div>

      {docs.length > 0 && (
        <div className="mt-6">
          <p className="text-[13px] font-500 text-[#6a6a6a] mb-2">Your uploaded documents</p>
          <div className="space-y-2">
            {docs.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-[#f0ece5]" data-testid="uploaded-doc-row">
                <span className="text-[13px] text-[#2b2b2b] flex items-center gap-2"><FileCheck2 size={15} className="text-green-600" /> {d.label} — {d.filename}</span>
                <a href={d.url} target="_blank" rel="noreferrer" className="text-[12px] text-[#b8935f] hover:underline">View</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default PortalCompliance;
